import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import { API_ENDPOINTS } from '../../config/env';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { getRoleDisplayLabel, MERCHANT_LABEL, SUB_MERCHANT_LABEL } from '../../utils/displayLabels';
import InternalPageHeader from '../Layout/InternalPageHeader';
import { useWindowRefresh } from '../../hooks/useWindowRefresh';
import { sortByNewest } from '../../utils/sortByNewest';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: 'Admin' | 'Agent' | 'SuperAgent';
  selectedSuperAgent?: string;
  usdtWallet: string;
}

type UserFormField = keyof UserFormData;
type UserFormErrors = Partial<Record<UserFormField, string>>;

interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  phone: string;
  settlementWallet: string;
  usdtWallet: string;
  role: 'Admin' | 'Agent' | 'SuperAgent';
  createdAt: string;
  registrationDate: string;
  createdSource: 'bot' | 'web';
  isActive?: boolean;
  parentId?: string;
  createdById?: number;
}

const extractApiErrorMessage = async (response: Response, fallbackMessage: string): Promise<string> => {
  const rawBody = await response.text();

  if (!rawBody.trim()) {
    return fallbackMessage;
  }

  try {
    const parsed = JSON.parse(rawBody) as
      | string
      | {
          message?: string;
          error?: { message?: string };
          expandedError?: { message?: string; technicalMessage?: string };
        };

    if (typeof parsed === 'string' && parsed.trim()) {
      return parsed;
    }

    if (typeof parsed === 'string') {
      return fallbackMessage;
    }

    return (
      parsed?.expandedError?.message ||
      parsed?.expandedError?.technicalMessage ||
      parsed?.error?.message ||
      parsed?.message ||
      fallbackMessage
    );
  } catch {
    return rawBody.trim() || fallbackMessage;
  }
};

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) {
    return null;
  }

  return (
    <div className="operator-field-error" role="alert">
      <span className="operator-field-error__eyebrow">Needs attention</span>
      <span>{message}</span>
    </div>
  );
};

const mapUserFromApi = (user: any): User => {
  const settlementWallet = user.usdcWallet || user.usdtWallet || '';
  const createdAt = user.createdAt || user.createdAtUtc || user.registrationDate || user.registrationDateUtc || '';
  const registrationDate = user.registrationDate || user.registrationDateUtc || createdAt;

  return {
    ...user,
    username: user.username || user.name || '',
    name: user.name || user.username || '',
    settlementWallet,
    usdtWallet: settlementWallet,
    createdAt,
    registrationDate,
  };
};

const compactLabel = (value: string, max = 26) => {
  const normalized = value.trim();
  if (normalized.length <= max) {
    return normalized;
  }

  const start = Math.max(0, max - 10);
  return `${normalized.slice(0, start)}...${normalized.slice(-7)}`;
};

const registrationDateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

interface DecodedToken {
  'X-SC-Role': string;
  'X-SC-ID': string;
  'X-SC-Session-ID': string;
  'X-SC-Nonce': string;
  'X-SC-TokenType': string;
  exp: number;
  iss: string;
  aud: string;
}

const UserManagement: React.FC = () => {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    role: 'SuperAgent', // Will be updated based on user role
    usdtWallet: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formErrors, setFormErrors] = useState<UserFormErrors>({});
  const [currentUser, setCurrentUser] = useState<string>('');
  const [currentUserRole, setCurrentUserRole] = useState<string>('');
  const [isFormExpanded, setIsFormExpanded] = useState<boolean>(false);
  const [isAccessExpanded, setIsAccessExpanded] = useState<boolean>(false);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [accountSearch, setAccountSearch] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [superAgents, setSuperAgents] = useState<User[]>([]);
  
  // Wallet edit modal state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletLoading, setWalletLoading] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Function to fetch SuperAgent users for dropdown
  const fetchSuperAgents = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}?role=SuperAgent`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const superAgentItems = Array.isArray(data)
        ? data
        : Array.isArray(data?.value)
          ? data.value
          : [];
      const mappedSuperAgents = superAgentItems.map(mapUserFromApi);
      setSuperAgents(mappedSuperAgents);
    } catch (error) {
      console.error('Error fetching SuperAgents:', error);
    }
  }, []);

  // Function to fetch users from API
  const fetchUsers = useCallback(async (silent: boolean = false) => {
    if (!silent) {
      setLoadingUsers(true);
    }

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const userRole = getCurrentUserRole();
      const userId = getCurrentUserId();
      
      // Build query parameters based on user role
      const params = new URLSearchParams();
      
      if (userRole === 'Admin') {
        // Admin can filter by role
        if (roleFilter) {
          params.append('role', roleFilter);
        }
      } else if (userRole === 'SuperAgent') {
        // SuperAgent can only see Agent users under them
        params.append('role', 'Agent');
        if (userId) {
          params.append('parentId', userId);
        }
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const userItems = Array.isArray(data)
        ? data
        : Array.isArray(data?.value)
          ? data.value
          : [];
      const mappedUsers = userItems.map(mapUserFromApi);
      setUsers(mappedUsers);
      if (!silent) {
        setMessage(current => current?.text === 'Failed to fetch users' ? null : current);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      if (!silent) {
        setMessage({ type: 'error', text: 'Failed to fetch users' });
      }
    } finally {
      if (!silent) {
        setLoadingUsers(false);
      }
    }
  }, [roleFilter]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const username = localStorage.getItem('username');
    
    if (token) {
      try {
        const decoded: DecodedToken = jwtDecode(token);
        // Use username if available, otherwise fall back to ID
        const displayUser = username || decoded['X-SC-ID'] || 'Unknown User';
        setCurrentUser(displayUser);
        setCurrentUserRole(decoded['X-SC-Role'] || '');
      } catch (error) {
        setCurrentUser(username || 'Unknown User');
        setCurrentUserRole('');
      }
    } else if (username) {
      setCurrentUser(username);
    }
  }, []);

  // Fetch users when component mounts or role filter changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch SuperAgents when component mounts
  useEffect(() => {
    fetchSuperAgents();
  }, [fetchSuperAgents]);

  useWindowRefresh(async () => {
    await fetchUsers(true);

    if (currentUserRole === 'Admin') {
      await fetchSuperAgents();
    }
  }, {
    enabled: true,
    intervalMs: 30000,
  });

  // Set default role based on current user role
  useEffect(() => {
    if (currentUserRole === 'SuperAgent') {
      setFormData(prev => ({ ...prev, role: 'Agent' }));
    } else if (currentUserRole === 'Admin') {
      setFormData(prev => ({ ...prev, role: 'SuperAgent' }));
    }
  }, [currentUserRole]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage(current => current?.type === 'error' ? null : current);
    setFormErrors(prev => {
      if (!prev[name as UserFormField]) {
        return prev;
      }

      const next = { ...prev };
      delete next[name as UserFormField];
      return next;
    });
  };

  const toggleForm = () => {
    setIsFormExpanded(!isFormExpanded);
    if (isFormExpanded) {
      // Reset form when collapsing
      const defaultRole = currentUserRole === 'SuperAgent' ? 'Agent' : 'SuperAgent';
      setFormData({ username: '', email: '', password: '', role: defaultRole, selectedSuperAgent: '', usdtWallet: '' });
      setMessage(null);
      setFormErrors({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setFormErrors({});

    try {
      const normalizedWallet = formData.usdtWallet.trim();

      if (!formData.username.trim()) {
        setFormErrors({ username: 'Username is required.' });
        throw new Error('Username is required.');
      }

      if (!formData.email.trim()) {
        setFormErrors({ email: 'Email is required.' });
        throw new Error('Email is required.');
      }

      if (!formData.password) {
        setFormErrors({ password: 'Password is required.' });
        throw new Error('Password is required.');
      }

      if (!isStrongPassword(formData.password)) {
        setFormErrors({ password: 'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.' });
        throw new Error('Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.');
      }

      if (!normalizedWallet) {
        setFormErrors({ usdtWallet: 'Wallet address is required.' });
        throw new Error('Wallet address is required.');
      }

      if (!isValidEthereumAddress(normalizedWallet)) {
        setFormErrors({ usdtWallet: 'Invalid wallet address format. Use a valid 0x EVM wallet address.' });
        throw new Error('Invalid wallet address format. Use a valid 0x EVM wallet address.');
      }

      if (formData.role === 'Agent' && currentUserRole === 'Admin' && !formData.selectedSuperAgent) {
      setFormErrors({ selectedSuperAgent: `Please select the ${MERCHANT_LABEL.toLowerCase()} this ${SUB_MERCHANT_LABEL.toLowerCase()} should belong to.` });
      throw new Error(`Please select the ${MERCHANT_LABEL.toLowerCase()} this ${SUB_MERCHANT_LABEL.toLowerCase()} should belong to.`);
      }

      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Prepare submission data according to new API format
      const submissionData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        usdcWallet: normalizedWallet, // Map frontend usdtWallet to backend usdcWallet
        createdById: formData.role === 'Agent' 
          ? (currentUserRole === 'SuperAgent' 
              ? parseInt(getCurrentUserId() || '0') 
              : parseInt(formData.selectedSuperAgent || '0'))
          : 0
      };

      const response = await fetch(API_ENDPOINTS.USERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to create user'));
      }

      const createdUserResponse = await response.json();
      const createdUser = mapUserFromApi(createdUserResponse);

      setMessage({ type: 'success', text: 'User created successfully!' });
      const defaultRole = currentUserRole === 'SuperAgent' ? 'Agent' : 'SuperAgent';
      setFormData({ username: '', email: '', password: '', role: defaultRole, selectedSuperAgent: '', usdtWallet: '' });
      setIsFormExpanded(false); // Collapse form after successful creation
      
      if (createdUser.role === 'SuperAgent') {
        setSuperAgents(prev => {
          const exists = prev.some(user => user.id === createdUser.id);
          return exists ? prev : [...prev, createdUser];
        });
      }

      await fetchUsers();

      if (currentUserRole === 'Admin') {
        await fetchSuperAgents();
      }
    } catch (error) {
      const errorText = error instanceof Error ? error.message : 'An error occurred while creating the user';
      const normalizedError = errorText.toLowerCase();

      if (normalizedError.includes('username')) {
        setFormErrors(prev => ({ ...prev, username: errorText }));
      } else if (normalizedError.includes('email')) {
        setFormErrors(prev => ({ ...prev, email: errorText }));
      } else if (normalizedError.includes('password')) {
        setFormErrors(prev => ({ ...prev, password: errorText }));
      } else if (normalizedError.includes('wallet')) {
        setFormErrors(prev => ({ ...prev, usdtWallet: errorText }));
      } else if (normalizedError.includes(MERCHANT_LABEL.toLowerCase()) || normalizedError.includes('merchant')) {
        setFormErrors(prev => ({ ...prev, selectedSuperAgent: errorText }));
      }

      setMessage({ 
        type: 'error', 
        text: errorText
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const variant = role === 'Admin' ? 'danger' : 
                   role === 'SuperAgent' ? 'warning' : 'info';
    return <span className={`badge bg-${variant}`}>{getRoleDisplayLabel(role)}</span>;
  };

  // ERC-20 wallet address validation
  const isValidEthereumAddress = (address: string): boolean => {
    // Basic Ethereum address validation (42 characters, starts with 0x, followed by 40 hex characters)
    const ethereumAddressRegex = /^0x[a-fA-F0-9]{40}$/;
    return ethereumAddressRegex.test(address);
  };

  const isStrongPassword = (password: string): boolean => {
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&._])[A-Za-z\d@$!%*?&._]{8,}$/;
    return passwordPattern.test(password);
  };

  const handleEditUser = (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      setWalletAddress(user.settlementWallet || '');
      setShowWalletModal(true);
    }
  };

  const handleCloseWalletModal = () => {
      setShowWalletModal(false);
      setSelectedUser(null);
      setWalletAddress('');
      setMessage(null);
      setFormErrors({});
  };

  const handleUpdateWallet = async () => {
    if (!selectedUser || !walletAddress.trim()) {
      setMessage({ type: 'error', text: 'Please enter a wallet address' });
      return;
    }

    if (!isValidEthereumAddress(walletAddress.trim())) {
      setMessage({ type: 'error', text: 'Please enter a valid ERC-20 wallet address' });
      return;
    }

    setWalletLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}/${selectedUser.id}/address`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newWalletAddress: walletAddress.trim()
        })
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, 'Failed to update wallet address'));
      }

      setMessage({ type: 'success', text: 'Wallet address updated successfully!' });
      
      // Update the user in the local state
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id 
          ? { ...user, settlementWallet: walletAddress.trim(), usdtWallet: walletAddress.trim() }
          : user
      ));
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseWalletModal();
      }, 1500);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'An error occurred while updating the wallet address' 
      });
    } finally {
      setWalletLoading(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setMessage(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.USERS}/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(await extractApiErrorMessage(response, `Failed to delete user. Status: ${response.status}`));
      }

      // Success - remove user from list
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      setMessage({ type: 'success', text: 'User deleted successfully!' });
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseDeleteModal();
      }, 1500);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'An error occurred while deleting the user' 
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const currentRoleLabel = currentUserRole ? getRoleDisplayLabel(currentUserRole) : 'Unknown role';
  const currentUserDisplayLabel = compactLabel(currentUser || 'Unknown user', 26);
  const provisioningScopeLabel = currentUserRole === 'Admin'
    ? `Can create ${MERCHANT_LABEL.toLowerCase()} and ${SUB_MERCHANT_LABEL.toLowerCase()} accounts`
    : `Can create ${SUB_MERCHANT_LABEL.toLowerCase()} accounts only`;
  const canManageAccounts = currentUserRole === 'Admin' || currentUserRole === 'SuperAgent';
  const defaultRoleLabel = currentUserRole === 'Admin' ? MERCHANT_LABEL : SUB_MERCHANT_LABEL;
  const normalizedAccountSearch = accountSearch.trim().toLowerCase();
  const visibleUsers = sortByNewest(
    users.filter((user) => {
      if (!normalizedAccountSearch) {
        return true;
      }

      const haystack = [
        user.name,
        user.username,
        user.email,
        user.settlementWallet,
        getRoleDisplayLabel(user.role),
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedAccountSearch);
    }),
    {
      dateKeys: ['registrationDate', 'createdAt'],
      numericFallbackKeys: ['id'],
    },
  );
  const merchantCount = visibleUsers.filter((user) => user.role === 'SuperAgent').length;
  const agentCount = visibleUsers.filter((user) => user.role === 'Agent').length;
  const walletConfiguredCount = visibleUsers.filter((user) => Boolean(user.settlementWallet)).length;
  const walletMissingCount = visibleUsers.length - walletConfiguredCount;

  const formatWalletPreview = (wallet?: string) => {
    if (!wallet) {
      return 'No payout wallet set';
    }

    if (wallet.length <= 14) {
      return wallet;
    }

    return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
  };

  const formatJoinedDate = (value?: string) => {
    if (!value?.trim()) {
      return 'Date unavailable';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return registrationDateFormatter.format(parsed);
  };

  const passwordFieldError = formErrors.password || (
    formData.password && !isStrongPassword(formData.password)
      ? 'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.'
      : undefined
  );

  const walletFieldError = formErrors.usdtWallet || (
    formData.usdtWallet.trim() && !isValidEthereumAddress(formData.usdtWallet.trim())
      ? 'Invalid wallet address format. Use a valid 0x EVM wallet address.'
      : undefined
  );

  return (
    <div className="operator-page">
      <Container fluid className="operator-page__container">
        <InternalPageHeader
          eyebrow="Access And Provisioning"
          title="User Management"
          description={`Create ${MERCHANT_LABEL.toLowerCase()} and ${SUB_MERCHANT_LABEL.toLowerCase()} accounts, set payout wallets, and keep access tidy.`}
          meta={[
            { label: 'Current operator', value: currentUserDisplayLabel },
            { label: 'Role scope', value: currentRoleLabel },
            { label: 'Visible users', value: users.length.toString() },
            { label: 'Provisioning', value: provisioningScopeLabel },
          ]}
        />
        {message && !showWalletModal && !showDeleteModal && (
          <Alert
            className="mt-4"
            variant={message.type === 'success' ? 'success' : 'danger'}
          >
            {message.text}
          </Alert>
        )}
        <Row className="g-4 mt-1">
          {canManageAccounts && (
            <Col xl={8}>
              <Card className="operator-card">
                <Card.Header
                  className="operator-card__header user-management__composer-header"
                  onClick={toggleForm}
                >
                  <div>
                    <div className="user-management__eyebrow">New Account</div>
                    <h4 className="operator-card__title mb-1">Create a new {defaultRoleLabel.toLowerCase()} account</h4>
                    <div className="operator-card__subtitle">
                      Capture login details, assign ownership, and set the payout wallet in one pass.
                    </div>
                  </div>
                  <span className="operator-toggle-indicator">{isFormExpanded ? '-' : '+'}</span>
                </Card.Header>
                {isFormExpanded && (
                  <Card.Body className="user-management__composer-body">
                    <div className="user-management__composer-note">
                      New accounts appear here right away, and wallet details can still be updated from the list.
                    </div>
                    {message?.type === 'error' && (
                      <Alert variant="danger" className="mb-3">
                        {message.text}
                      </Alert>
                    )}
                    <Form onSubmit={handleSubmit}>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                              className="operator-control"
                              type="text"
                              name="username"
                              value={formData.username}
                              onChange={handleInputChange}
                              placeholder="Choose a username"
                              isInvalid={Boolean(formErrors.username)}
                              required
                            />
                            <FieldError message={formErrors.username} />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                              className="operator-control"
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="name@company.com"
                              isInvalid={Boolean(formErrors.email)}
                              required
                            />
                            <FieldError message={formErrors.email} />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                              className="operator-control"
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              placeholder="Create a temporary password"
                              isInvalid={Boolean(passwordFieldError)}
                              required
                              minLength={8}
                            />
                            <Form.Text>Use 8+ characters with uppercase, lowercase, a number, and a symbol.</Form.Text>
                            <FieldError message={passwordFieldError} />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Account type</Form.Label>
                            <Form.Select
                              className="operator-control"
                              name="role"
                              value={formData.role}
                              onChange={handleInputChange}
                              required
                            >
                              {currentUserRole === 'Admin' && (
                                <>
                                  <option value="SuperAgent">{MERCHANT_LABEL}</option>
                                  <option value="Agent">{SUB_MERCHANT_LABEL}</option>
                                </>
                              )}
                              {currentUserRole === 'SuperAgent' && (
                                <option value="Agent">{SUB_MERCHANT_LABEL}</option>
                              )}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Polygon payout wallet</Form.Label>
                            <Form.Control
                              className="operator-control"
                              type="text"
                              name="usdtWallet"
                              value={formData.usdtWallet}
                              onChange={handleInputChange}
                              placeholder="0x..."
                              isInvalid={Boolean(walletFieldError)}
                              required
                            />
                            <FieldError message={walletFieldError} />
                            <Form.Text>Use the Polygon wallet that should receive this account's payouts.</Form.Text>
                          </Form.Group>
                        </Col>
                        {formData.role === 'Agent' && (
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Label>{MERCHANT_LABEL}</Form.Label>
                              {currentUserRole === 'SuperAgent' ? (
                                <>
                                  <Form.Control
                                    className="operator-control"
                                    type="text"
                                    value={currentUser}
                                    readOnly
                                  />
                                  <Form.Text>This {SUB_MERCHANT_LABEL.toLowerCase()} will be assigned to your {MERCHANT_LABEL.toLowerCase()} account automatically.</Form.Text>
                                </>
                              ) : (
                                <>
                                  <Form.Select
                                    className="operator-control"
                                    name="selectedSuperAgent"
                                    value={formData.selectedSuperAgent || ''}
                                    onChange={handleInputChange}
                                    isInvalid={Boolean(formErrors.selectedSuperAgent)}
                                    required
                                  >
                                    <option value="">Select {MERCHANT_LABEL}</option>
                                    {superAgents.map((superAgent) => (
                                      <option key={superAgent.id} value={superAgent.id}>
                                        {superAgent.username} ({superAgent.email})
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Text>Choose the {MERCHANT_LABEL.toLowerCase()} account this {SUB_MERCHANT_LABEL.toLowerCase()} belongs to.</Form.Text>
                                  <FieldError message={formErrors.selectedSuperAgent} />
                                </>
                              )}
                            </Form.Group>
                          </Col>
                        )}
                      </Row>

                      <div className="user-management__composer-actions">
                        <Button
                          variant="outline-light"
                          type="button"
                          onClick={toggleForm}
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                          {loading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Creating account...
                            </>
                          ) : (
                            'Create account'
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                )}
              </Card>
            </Col>
          )}
          <Col xl={canManageAccounts ? 4 : 12}>
            <Card className="operator-card h-100">
              <Card.Header
                className="operator-card__header user-management__composer-header"
                onClick={() => setIsAccessExpanded((previous) => !previous)}
              >
                <div>
                  <div className="user-management__eyebrow">Your access</div>
                  <h5 className="operator-card__title mb-1">{currentRoleLabel}</h5>
                  <div className="operator-card__subtitle">
                     Open this when you need scope, wallet readiness, and quick actions.
                  </div>
                </div>
                <span className="operator-toggle-indicator">{isAccessExpanded ? '-' : '+'}</span>
              </Card.Header>
              {isAccessExpanded && (
                <Card.Body className="user-management__overview">
                  <div className="operator-current-user">
                    <div className="operator-current-user__label">Logged in as</div>
                    <div className="operator-current-user__name">{currentUserDisplayLabel}</div>
                    <div className="operator-current-user__role">{currentRoleLabel}</div>
                  </div>
                  <div className="operator-summary-grid">
                    <div className="operator-summary-card" style={{ ['--summary-accent' as string]: '#0088cc' }}>
                      <div className="operator-summary-card__label">Visible Accounts</div>
                      <div className="operator-summary-card__value">{visibleUsers.length}</div>
                      <div className="operator-summary-card__note">Accounts in your current view.</div>
                    </div>
                    <div className="operator-summary-card" style={{ ['--summary-accent' as string]: '#979797' }}>
                      <div className="operator-summary-card__label">Wallets Ready</div>
                      <div className="operator-summary-card__value">{walletConfiguredCount}</div>
                      <div className="operator-summary-card__note">
                        {walletMissingCount > 0 ? `${walletMissingCount} account${walletMissingCount === 1 ? '' : 's'} still need payout wallets.` : 'Every visible account has a payout wallet.'}
                      </div>
                    </div>
                  </div>
                  <div className="user-management__tips">
                    <div className="user-management__tip-title">Quick actions</div>
                      <div className="user-management__tip-item">Create new {currentUserRole === 'Admin' ? `${MERCHANT_LABEL.toLowerCase()} and ${SUB_MERCHANT_LABEL.toLowerCase()}` : SUB_MERCHANT_LABEL.toLowerCase()} accounts without leaving this page.</div>
                    <div className="user-management__tip-item">Update payout wallets from the account list whenever routing changes.</div>
                    {currentUserRole === 'Admin' && (
                      <div className="user-management__tip-item">Remove accounts you no longer want active after reviewing their details.</div>
                    )}
                  </div>
                </Card.Body>
              )}
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Card className="operator-card">
              <Card.Header className="operator-card__header">
                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                  <div>
                    <h5 className="operator-card__title">Account workspace</h5>
                    <div className="operator-card__subtitle">
                      Search, filter, and update payout details from one workspace.
                    </div>
                  </div>
                  <div className="user-management__toolbar">
                    <Form.Control
                      className="operator-control user-management__search"
                      type="search"
                      placeholder="Search by name, email, role, or wallet"
                      value={accountSearch}
                      onChange={(e) => setAccountSearch(e.target.value)}
                    />
                    {currentUserRole === 'Admin' && (
                      <Form.Select
                        className="operator-control user-management__role-filter"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                      >
                        <option value="">All roles</option>
                        <option value="Admin">Admin</option>
                        <option value="SuperAgent">{MERCHANT_LABEL}</option>
                        <option value="Agent">{SUB_MERCHANT_LABEL}</option>
                      </Form.Select>
                    )}
                    <Button variant="outline-light" onClick={() => void fetchUsers()} disabled={loadingUsers}>
                      Refresh
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="operator-summary-grid user-management__summary-grid">
                  <div className="operator-summary-card" style={{ ['--summary-accent' as string]: '#0088cc' }}>
                    <div className="operator-summary-card__label">Accounts In View</div>
                    <div className="operator-summary-card__value">{visibleUsers.length}</div>
                    <div className="operator-summary-card__note">Current search and role filters applied.</div>
                  </div>
                  <div className="operator-summary-card" style={{ ['--summary-accent' as string]: '#979797' }}>
                    <div className="operator-summary-card__label">{MERCHANT_LABEL}s</div>
                    <div className="operator-summary-card__value">{merchantCount}</div>
                    <div className="operator-summary-card__note">{currentUserRole === 'SuperAgent' ? 'Your own account appears here when it matches the current view.' : `Accounts with ${MERCHANT_LABEL.toLowerCase()} access.`}</div>
                  </div>
                  <div className="operator-summary-card" style={{ ['--summary-accent' as string]: '#666666' }}>
                    <div className="operator-summary-card__label">{SUB_MERCHANT_LABEL}s</div>
                    <div className="operator-summary-card__value">{agentCount}</div>
                    <div className="operator-summary-card__note">Accounts that work under a {MERCHANT_LABEL.toLowerCase()}.</div>
                  </div>
                </div>

                {loadingUsers ? (
                  <div className="app-empty-state">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 mb-0">Loading accounts...</p>
                  </div>
                ) : visibleUsers.length === 0 ? (
                  <div className="app-empty-state">
                    <p className="mb-0">No accounts match this view yet.</p>
                  </div>
                ) : (
                  <div className="user-management__list">
                    {visibleUsers.map((user) => (
                      <article className="user-management__account-row" key={user.id}>
                        <div className="user-management__account-main">
                          <div className="user-management__account-top">
                            <div>
                              <div className="user-management__account-name">{user.name || user.username}</div>
                              <div className="user-management__account-email">{user.email}</div>
                            </div>
                            {getRoleBadge(user.role)}
                          </div>
                          <div className="user-management__account-grid">
                            <div className="user-management__account-detail">
                              <span className="user-management__account-label">User ID</span>
                              <strong className="settings-mono-value">{user.id}</strong>
                            </div>
                            <div className="user-management__account-detail">
                              <span className="user-management__account-label">Username</span>
                              <strong>{user.username || 'Not set'}</strong>
                            </div>
                            <div className="user-management__account-detail">
                              <span className="user-management__account-label">Payout wallet</span>
                              <strong className={user.settlementWallet ? 'settings-mono-value' : ''}>{formatWalletPreview(user.settlementWallet)}</strong>
                            </div>
                            <div className="user-management__account-detail">
                              <span className="user-management__account-label">Joined</span>
                              <strong>{formatJoinedDate(user.registrationDate || user.createdAt)}</strong>
                            </div>
                          </div>
                        </div>
                        {canManageAccounts && (
                          <div className="user-management__account-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditUser(user.id)}
                            >
                              Update wallet
                            </Button>
                            {currentUserRole === 'Admin' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteClick(user)}
                              >
                                Delete account
                              </Button>
                            )}
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Wallet Edit Modal */}
      <Modal
        show={showWalletModal}
        onHide={handleCloseWalletModal}
        centered
        dialogClassName="user-management__modal-dialog"
        contentClassName="operator-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Update payout wallet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && (
            <Alert variant={message.type === 'success' ? 'success' : 'danger'}>
              {message.text}
            </Alert>
          )}

          <p className="operator-muted-text">
            Choose the Polygon wallet that should receive payouts for {selectedUser?.name || 'this account'}.
          </p>

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Polygon payout wallet</Form.Label>
              <Form.Control
                className="operator-control"
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="0x..."
              />
              <Form.Text>
                Use a valid EVM wallet address that starts with 0x.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" onClick={handleCloseWalletModal}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdateWallet}
            disabled={walletLoading}
          >
            {walletLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              'Update Wallet'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        dialogClassName="user-management__modal-dialog"
        contentClassName="operator-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete account?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && (
            <Alert variant={message.type === 'success' ? 'success' : 'danger'}>
              {message.text}
            </Alert>
          )}

          <p className="mb-2">
            Remove this account only if you are sure it should no longer be available for sign-in or payout routing.
          </p>
          {userToDelete && (
            <div className="operator-muted-text mt-3">
              <strong>Account:</strong> {userToDelete.name} ({userToDelete.email})
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" onClick={handleCloseDeleteModal} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteUser}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              'Yes, Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;


